// Core terrain constants

/**
 * Global seed system for procedural generation
 * The seed can be set via URL parameter 'seed' or will be random
 */
export const DEFAULT_SEED = Math.floor(Math.random() * 1000000);

/**
 * Get the active seed from URL parameters or generate random
 * @returns {number} The active seed value
 */
export function getActiveSeed() {
    // Check URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const seedParam = urlParams.get('seed');
    const forceParam = urlParams.get('_t'); // Check if this is a forced generation from UI
    
    // Generate random seed
    const randomSeed = Math.floor(Math.random() * 1000000);
    
    // Only use URL seed if explicitly set by UI with force parameter
    if (seedParam && forceParam) {
        const parsedSeed = parseInt(seedParam, 10);
        return isNaN(parsedSeed) ? hashString(seedParam) : parsedSeed;
    }
    
    return randomSeed;
}

/**
 * Generate a random seed if none is provided
 * @returns {number} A random seed value
 */
export function generateRandomSeed() {
    return Math.floor(Math.random() * 1000000);
}

/**
 * Derive a sub-seed for a specific system
 * @param {string} system - The system name (e.g., 'terrain', 'biomes', 'structures')
 * @returns {number} A derived seed value for the system
 */
export function getSystemSeed(system) {
    const mainSeed = getActiveSeed();
    const systemHash = system.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return mainSeed + systemHash;
}

/**
 * Helper function to convert string to numeric seed
 * @param {string} str - The string to hash
 * @returns {number} A numeric hash of the string
 */
export function hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
}

// Log the active seed at startup
console.log(`[Terrain] Active seed: ${getActiveSeed()}`);

// World size and chunk configuration
export const CHUNK_SIZE = 32;
export const WORLD_SIZE_IN_CHUNKS = 9;
export const MAP_RADIUS = 144;
export const WORLD_CHUNKS_ACROSS = WORLD_SIZE_IN_CHUNKS;
export const WORLD_SIZE = CHUNK_SIZE * WORLD_CHUNKS_ACROSS;

// Fixed spawn points for testing
export const FIXED_POINTS = [
    { x: MAP_RADIUS, z: MAP_RADIUS },      // Center
    { x: MAP_RADIUS - 64, z: MAP_RADIUS }, // West
    { x: MAP_RADIUS + 64, z: MAP_RADIUS }, // East
    { x: MAP_RADIUS, z: MAP_RADIUS - 64 }, // North
    { x: MAP_RADIUS, z: MAP_RADIUS + 64 }  // South
];

export const TEST_POINTS = [
    { x: MAP_RADIUS - 96, z: MAP_RADIUS - 32 },
    { x: MAP_RADIUS - 96, z: MAP_RADIUS + 32 },
    { x: MAP_RADIUS - 96, z: MAP_RADIUS },
    { x: MAP_RADIUS - 96, z: MAP_RADIUS - 16 }
];

// Player spawn and initial loading
export const PLAYER_SPAWN = {
    TEST_POINTS: TEST_POINTS,
    RANDOM_CONSTRAINTS: {
        MIN_DISTANCE_FROM_CENTER: 0.2,
        MAX_DISTANCE_FROM_CENTER: 0.7,
        MIN_HEIGHT: 1,
        MAX_SLOPE: 1.0
    }
};
export const INITIAL_CHUNK_LOAD_RADIUS = 3;

// Water and terrain configuration
export const WATER_LEVEL = 4;
export const WATER_BORDER = 30;
export const BASE_HEIGHT = 8;
export const MOUNTAIN_HEIGHT = 45;

// Height and terrain constants
export const MIN_TERRAIN_HEIGHT = 2;
export const MAX_HEIGHT_VARIATION = 22;
export const STONE_LEVEL = 3;

// Distance and view constants
export const VIEW_DISTANCE = 2;
export const VOXEL_SIZE = 1;
export const ISLAND_RADIUS = MAP_RADIUS - 20;

// Terrain generation settings
export const DEFAULT_TERRAIN_SETTINGS = {
    amplitude: 20,
    frequency: 0.018,
    baseHeight: BASE_HEIGHT,
    variation: MAX_HEIGHT_VARIATION,
    scale: 1,
    smoothness: 0.9,
    edgeSharpness: 0.85,
    noiseScale: 0.035
};

// Island shape types
export const ISLAND_SHAPES = {
    CIRCULAR: 0,
    IRREGULAR: 1,
    CRESCENT: 2,
    VOLCANIC: 3,
    ATOLL: 4,
    ARCHIPELAGO: 5
};

// Island shape names mapping
export const ISLAND_SHAPE_NAMES = {
    0: 'Circular Island',
    1: 'Irregular Island',
    2: 'Crescent Island',
    3: 'Volcanic Island',
    4: 'Atoll Island',
    5: 'Archipelago'
};

// Function to get island shape type from URL or random
export function getIslandShapeFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const typeParam = urlParams.get('islandType');
    const forceParam = urlParams.get('_t'); // Check if this is a forced generation from UI
    
    // Only use specified type if explicitly set by UI with force parameter
    if (typeParam !== null && forceParam) {
        const type = parseInt(typeParam);
        // Validate type and ensure it's not Crescent (2)
        if (!isNaN(type) && type >= 0 && type <= 5 && type !== ISLAND_SHAPES.CRESCENT) {
            window.selectedIslandType = type;
            return type;
        } else if (type === ISLAND_SHAPES.CRESCENT) {
            console.warn('[Terrain] Crescent Island type (2) is disabled, generating a random valid type instead.');
        }
    }
    
    // Generate random island type (excluding Crescent)
    const randomType = getRandomIslandType(); // Use the updated function
    window.selectedIslandType = randomType;
    return randomType;
}

// Function to get a random island type (0-5), excluding Crescent (2)
export function getRandomIslandType() {
    const validTypes = [0, 1, 3, 4, 5]; // Exclude 2 (Crescent)
    const randomIndex = Math.floor(Math.random() * validTypes.length);
    return validTypes[randomIndex];
}

// Biome types
export const BIOMES = {
    OCEAN: 'ocean',
    SHORE: 'shore',
    PLAINS: 'plains',
    MOUNTAINS: 'mountains',
    FACTION: 'faction',
    LAKE: 'lake'
};

// Physics settings for terrain
export const TERRAIN_PHYSICS = {
    gravity: -9.82,
    friction: 0.5,
    restitution: 0.1
};

// Spawn point configuration
export const SPAWN_CONFIG = {
    MODE: {
        RANDOM: 'random',
        FIXED: 'fixed',
        TEST: 'test'
    },
    FIXED_POINTS: FIXED_POINTS,
    TEST_POINTS: TEST_POINTS,
    RANDOM_CONSTRAINTS: {
        MIN_DISTANCE_FROM_CENTER: 0.0,  // Allow spawning anywhere
        MAX_DISTANCE_FROM_CENTER: 0.95, // Almost to the edge of the island
        MIN_HEIGHT: 1,
        MAX_SLOPE: 1.0
    }
};

export const ISLAND_TYPE_SETTINGS = {
    [ISLAND_SHAPES.CIRCULAR]: {
        edgeSharpness: 1.6,
        noiseScale: 0.035,
        mountainHeight: 1.1,
        mountainSize: 0.18
    },
    [ISLAND_SHAPES.IRREGULAR]: {
        edgeSharpness: 1.3,
        noiseScale: 0.055,
        mountainHeight: 1.0,
        mountainSize: 0.20
    },
    /* // Crescent Island is buggy, disable for now
    [ISLAND_SHAPES.CRESCENT]: {
        edgeSharpness: 2.0,
        noiseScale: 0.03,
        mountainHeight: 1.7,
        mountainSize: 0.12
    },
    */
    [ISLAND_SHAPES.VOLCANIC]: {
        edgeSharpness: 2.0,
        noiseScale: 0.025,
        mountainHeight: 1.25,
        mountainSize: 0.15
    },
    [ISLAND_SHAPES.ATOLL]: {
        edgeSharpness: 1.4,
        noiseScale: 0.045,
        mountainHeight: 0.7,
        mountainSize: 0.25
    },
    [ISLAND_SHAPES.ARCHIPELAGO]: {
        edgeSharpness: 1.1,
        noiseScale: 0.07,
        mountainHeight: 1.0,
        mountainSize: 0.20
    }
};
